<template>
  <nav
    ref="navContainer"
    class="hidden lg:flex items-center h-[48px] px-3 bg-white dark:bg-gray-800 w-full max-w-full relative select-none"
  >
    <!-- Loader -->
    <div v-if="loading" class="flex items-center gap-2 text-gray-400 text-xs px-2">
      <Icon icon="mdi:loading" class="animate-spin" width="16" height="16" />
      <span>Cargando módulos...</span>
    </div>

    <!-- Sin menú -->
    <div v-else-if="!menuTree || menuTree.length === 0" class="text-xs text-gray-400 italic px-2">
      Sin módulos asignados
    </div>

    <!-- Contenedor adaptativo de módulos (sin overflow-hidden para que los menús desplegables floten libres) -->
    <template v-else>
      <div
        ref="itemsList"
        class="flex items-center gap-1 min-w-0"
      >
        <MenuNode
          v-for="item in modulosVisibles"
          :key="item.id"
          :item="item"
          :collapsed="false"
          :level="0"
          :active-section="activeSection"
          orientation="horizontal"
          @item-click="handleItemClick"
          @section-opened="handleSectionOpened"
        />
      </div>

      <!-- Botón "Más" (dropdown con los módulos que no caben en pantalla) -->
      <div v-if="modulosOcultos.length > 0" class="relative ml-auto pl-2 flex-shrink-0 z-[250]">
        <button
          @click.stop="toggleMasMenu"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all shadow-sm whitespace-nowrap cursor-pointer"
          :class="[
            masMenuAbierto
              ? 'bg-purple-600 text-white shadow-purple-200 dark:shadow-none'
              : 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 border border-purple-200 dark:border-purple-800'
          ]"
          title="Ver módulos adicionales que no caben en la barra"
        >
          <Icon icon="mdi:dots-vertical" width="16" height="16" />
          <span>Más</span>
          <span
            class="text-[10px] rounded-full px-1.5 py-0.2 font-black shadow-xs"
            :class="masMenuAbierto ? 'bg-white text-purple-700' : 'bg-purple-600 text-white'"
          >
            {{ modulosOcultos.length }}
          </span>
        </button>

        <transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1 scale-95"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 -translate-y-1 scale-95"
        >
          <div
            v-if="masMenuAbierto"
            class="absolute top-full right-0 mt-2 min-w-[240px] max-w-[90vw] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 py-1.5 z-[9999] max-h-[75vh] overflow-y-auto"
          >
            <div class="px-3 py-1 text-[10px] font-black uppercase text-gray-400 tracking-wider border-b border-gray-100 dark:border-gray-700 mb-1">
              Módulos Adicionales ({{ modulosOcultos.length }})
            </div>
            <MenuNode
              v-for="item in modulosOcultos"
              :key="item.id"
              :item="item"
              :collapsed="false"
              :level="0"
              :active-section="activeSection"
              orientation="horizontal"
              :force-vertical="true"
              @item-click="handleItemClickCerrado"
              @section-opened="handleSectionOpened"
            />
          </div>
        </transition>
      </div>
    </template>
  </nav>
</template>

<script>
import { Icon } from "@iconify/vue";
import MenuNode from "./MenuNode.vue";

export default {
  name: 'SidebarMenu',
  components: { Icon, MenuNode },
  props: {
    collapsed: { type: Boolean, default: false },
    menuTree:  { type: Array,   default: () => [] },
    loading:   { type: Boolean, default: false },
  },
  data() {
    return {
      activeSection: null,
      masMenuAbierto: false,
      limiteVisibles: 5,
      itemWidths: {},
      resizeObserver: null,
    };
  },
  computed: {
    listaNormalizada() {
      return Array.isArray(this.menuTree) ? this.menuTree.filter(Boolean) : [];
    },
    modulosVisibles() {
      const lista = this.listaNormalizada;
      return lista.length <= this.limiteVisibles
        ? lista
        : lista.slice(0, this.limiteVisibles);
    },
    modulosOcultos() {
      const lista = this.listaNormalizada;
      return lista.length <= this.limiteVisibles
        ? []
        : lista.slice(this.limiteVisibles);
    }
  },
  watch: {
    menuTree: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.medirYRecalcular();
        });
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.cerrarMasMenuSiClickFuera);
    this.iniciarObservador();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.cerrarMasMenuSiClickFuera);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.medirYRecalcular);
  },
  methods: {
    iniciarObservador() {
      this.$nextTick(() => {
        this.medirYRecalcular();
        if (typeof ResizeObserver !== 'undefined' && this.$refs.navContainer) {
          this.resizeObserver = new ResizeObserver(() => {
            this.medirYRecalcular();
          });
          this.resizeObserver.observe(this.$refs.navContainer);
        }
        window.addEventListener('resize', this.medirYRecalcular);
      });
    },
    medirYRecalcular() {
      const lista = this.listaNormalizada;
      if (lista.length === 0) {
        this.limiteVisibles = 0;
        return;
      }

      // Medir anchos reales de los elementos ya presentes en el DOM
      if (this.$refs.navContainer) {
        const elements = this.$refs.navContainer.querySelectorAll('.menu-node-item');
        elements.forEach(el => {
          const id = el.getAttribute('data-id');
          if (id) {
            const w = el.getBoundingClientRect().width;
            if (w > 30) {
              this.itemWidths[id] = Math.ceil(w);
            }
          }
        });
      }

      // Ancho máximo disponible en el viewport (descontando el sidebar izquierdo de 72px + márgenes)
      const leftSidebarWidth = window.innerWidth >= 1024 ? 76 : 0;
      const viewportAvailable = Math.max(300, window.innerWidth - leftSidebarWidth - 28);
      
      const containerWidth = this.$refs.navContainer?.clientWidth || viewportAvailable;
      const navWidth = Math.min(viewportAvailable, containerWidth);

      const getItemWidth = (item) => {
        if (this.itemWidths[item.id]) return this.itemWidths[item.id];
        const textLen = (item.nombre || '').length;
        // 16px icono + 6px gap + text + 14px flecha + 20px padding + 4px margen
        return Math.max(130, Math.min(240, textLen * 8.2 + 65));
      };

      // 1. ¿Caben TODOS los módulos holgadamente?
      let totalTodos = 0;
      for (const item of lista) {
        totalTodos += getItemWidth(item) + 4;
      }

      if (totalTodos <= navWidth - 10) {
        this.limiteVisibles = lista.length;
        return;
      }

      // 2. Si no caben todos, reservamos espacio para el botón "Más" (~95px)
      const espacioDisponible = Math.max(100, navWidth - 105);
      let acumulado = 0;
      let visibles = 0;

      for (let i = 0; i < lista.length; i++) {
        const item = lista[i];
        const w = getItemWidth(item) + 4;
        if (acumulado + w <= espacioDisponible) {
          acumulado += w;
          visibles++;
        } else {
          break;
        }
      }

      this.limiteVisibles = Math.max(1, visibles);
    },
    toggleMasMenu() {
      this.masMenuAbierto = !this.masMenuAbierto;
    },
    cerrarMasMenuSiClickFuera(e) {
      if (!this.masMenuAbierto) return;
      if (e.target.closest('.relative.ml-auto')) return;
      this.masMenuAbierto = false;
    },
    handleItemClickCerrado(item) {
      this.handleItemClick(item);
      this.masMenuAbierto = false;
    },
    handleSectionOpened(sectionName) {
      this.activeSection = sectionName;
    },
    handleItemClick(item) {
      if (!item) return;
      if (item.ruta && /^https?:\/\//i.test(item.ruta)) {
        window.open(item.ruta, '_blank', 'noopener');
      }
    },
  }
};
</script>
